import { Component, Input, OnInit } from "@angular/core";
import { SettlementsService } from "@app/settlements/shared/settlements.service";
import { ToastrService } from "ngx-toastr";
import { DeductionService } from "../../deduction.service";

@Component({
  selector: "app-settlement-teams-tab",
  templateUrl: "./settlement-teams-tab.component.html",
  styleUrls: ["./settlement-teams-tab.component.css"],
})
export class SettlementTeamsTabComponent implements OnInit {
  @Input("batchStatus") batchStatus: string;
  @Input("batchId")
  set batchId(batchId: number) {
    this._batchId = batchId;
    if (batchId) this.loadTeamSplits(batchId);
  }
  deductions: any = [];
  splits: any = [];
  totalSplits: any = [];
  drivers: any = [];
  totals: any = {};
  baseTableWidth: number = 400;
  tableWidth: number = this.baseTableWidth;
  driverColumnWidth: number = 200;
  invalidTotal: boolean = false;
  _batchId: number;

  cols = [
    { field: "chargeType", header: "Charge Type" },
    { field: "total", header: "Total" },
    { field: "tripNo", header: "Trip" },
  ];

  constructor(
    private _SettlementsService: SettlementsService,
    private toastr: ToastrService,
    private DeductionService: DeductionService
  ) {}

  ngOnInit(): void {}

  recalculateTotals(): void {
    this.calculateCustomTotals();
    this.calculateTeamTotals();
  }

  getPayoutAmounts(split, driverId) {
    if (split.payoutAmounts) {
      return split.payoutAmounts.find((m) => m.driverId == driverId);
    }
  }

  calculateCustomTotals(): void {
    this.invalidTotal = false;
    for (const split of this.splits) {
      let total = 0;
      for (const driver of this.drivers) {
        const customValue = split.payoutAmounts.find((m) => m.driverId == driver.id).customValue;
        total += Math.round(customValue * 100) / 100;
      }
      // round to 2 decimal places
      split.customTotal = Math.round(total * 100) / 100;
      if (split.customTotal != split.total) this.invalidTotal = true;
    }
  }

  calculateTeamTotals(): void {
    this.totals = {};
    for (const split of this.splits) {
      for (const driver of this.drivers) {
        const customValue = split.payoutAmounts.find((m) => m.driverId == driver.id).customValue;
        if (this.totals[driver.id]) {
          this.totals[driver.id] += customValue;
        } else {
          this.totals[driver.id] = customValue;
        }
      }
    }
    this.totals.total = this.splits.reduce((acc, curr) => acc + curr.total, 0);

    this.calculateTotalSplits();
  }

  calculateTotalSplits(): void {
    // Total splits are the same as splits, but with each unique charge type
    const uniqueChargeTypes = new Set(this.splits.map((split) => split.chargeType.id));
    this.totalSplits = Array.from(uniqueChargeTypes).map((chargeTypeId) => {
      const splits = this.splits.filter((split) => split.chargeType.id === chargeTypeId);

      // sum the payout amounts for each driver
      const payoutAmounts = [];
      for (const driver of this.drivers) {
        const customValue = splits.reduce((acc, split) => {
          const payout = split.payoutAmounts.find((m) => m.driverId == driver.id);
          return acc + (payout ? payout.customValue : 0);
        }, 0);
        const originalValue = splits.reduce((acc, split) => {
          const payout = split.payoutAmounts.find((m) => m.driverId == driver.id);
          return acc + (payout ? payout.originalValue : 0);
        }, 0);
        payoutAmounts.push({ driverId: driver.id, customValue, originalValue });
      }

      const split = {
        chargeTypeId,
        chargeType: splits[0].chargeType,
        customTotal: splits.reduce((acc, split) => acc + split.customTotal, 0),
        total: splits.reduce((acc, split) => acc + split.total, 0),
        payoutAmounts
      };
      return split;
    });
  }

  loadTeamSplits(batchDriverId: number): void {
    this.DeductionService.getDeductionDropdown().subscribe((data) => {
      this.deductions = data;
    });
    this._SettlementsService.getTeamSplitByBatchDriverId(batchDriverId).subscribe(
      (data: any) => {
        const { splits, drivers, totals } = data;
        this.splits = splits;
        for (const split of this.splits) {
          const { chargeType } = split;
          for (const amount of split.payoutAmounts) {
            amount.startingValue = amount.customValue;
          }
          if (chargeType.name == "DEFADD") chargeType.name = "DEFICIT SUBTOTAL";
          if (chargeType.name == "ESCADD") chargeType.name = "ESCROW SUBTOTAL";
        }
        this.drivers = drivers;
        this.totals = totals;
        if (!this.totals) this.totals = { total: 0 };
        this.tableWidth = this.baseTableWidth + this.drivers.length * this.driverColumnWidth;

        this.drivers.sort((a, b) => b.earningPercentage - a.earningPercentage);

        const teamDriverColumns = drivers.map((driver) => {
          const header = `${driver.name} ${driver.lastName}`;
          return { field: driver.id, header: header };
        });

        this.cols = [...this.cols, ...teamDriverColumns];

        this.recalculateTotals();
      },
      (error) => {
        this.toastr.error(error.message, "Error loading team splits");
      }
    );
  }

  validate(): boolean {
    this.recalculateTotals();
    for (const split of this.splits) {
      if (split.total !== split.customTotal) {
        this.toastr.error(`Team Splits are invalid`, "Error");
        return false;
      }
    }

    return true;
  }

  getOverrides(): any {
    const overrides = [];
    for (const split of this.splits) {
      for (const driver of this.drivers) {
        const { customValue, startingValue } = this.getPayoutAmounts(split, driver.id);
        if (customValue !== startingValue) {
          overrides.push({
            BatchId: this._batchId,
            ChargeTypeId: split.chargeType.id,
            DriverId: driver.id,
            TripId: split.tripId,
            customValue: this.getPayoutAmounts(split, driver.id).customValue,
          });
        }
      }
    }
    return overrides;
  }
}
